import React, { FC, useEffect } from 'react';
import styled from 'styled-components';

import { H2 } from '@ad-scripts/presentation/components/common/headers';
import ModalCard from '@ad-scripts/presentation/components/common/modal_card';
import { PortalModalWrapper } from 'src/presentation/components/agent_desktop_components';

import XIcon from '@ad-scripts/presentation/components/lib/icons/x_icon';
import StackContainer from '@ad-scripts/presentation/components/common/containers/stack_container';

export enum ModalSize {
  DEFAULT = '450px',
  MEDIUM = '500px',
  LARGE = '740px',
  EXTRALARGE = '800px',
}

type EmbedProps = {
  'data-aid'?: string;
  content?: any;
  modalSize?: ModalSize;
  onCancel: Function;
  props?: any;
  disableOutsideClick?: boolean;
  showCloseButton?: boolean;
  title?: string;
};

const EmbedModal: FC<EmbedProps> = ({
  'data-aid': dataAid,
  title,
  content,
  onCancel,
  modalSize = ModalSize.DEFAULT,
  disableOutsideClick = false,
  showCloseButton = true,
  ...props
}) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const onCancelHandler = disableOutsideClick ? undefined : onCancel;

  return (
    <ModalWrap {...props}>
      <Modal data-aid={dataAid ? dataAid : 'embedModal'}>
        <EmbedCard onClose={onCancelHandler} modalSize={modalSize}>
          {showCloseButton && (
            <Close>
              <CloseIcon data-aid="embedModal-close" onClick={onCancel} />
            </Close>
          )}
          {title && <Header data-aid="embedModal-title">{title}</Header>}
          {content}
        </EmbedCard>
      </Modal>
    </ModalWrap>
  );
};

const ModalWrap = styled(PortalModalWrapper)`
  .Modal-backdrop {
    background-color: rgba(0, 0, 0, 0.6);
  }

  .Modal-backdrop,
  .Modal-wrapper {
    align-items: center;
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    overflow: hidden;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 2;
  }

  .modalCard-backdrop {
    align-items: center;
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 3;
  }

  .modalCard-content {
    background-color: ${p => p.theme.colors.white};
    border-radius: 6px;
    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
    padding: 15px 20px 5px 20px;
  }
`;

const EmbedCard = styled(ModalCard)`
  padding: ${p => p.theme.spacing.large};
  width: ${p => p.modalSize};
`;

export const Header = styled(H2)`
  justify-content: space-around;
`;

export const Close = styled(StackContainer).attrs({ inset: 'none', stack: 'small' })`
  width: 100%;
  height: 16px;
  display: flex;
  flex-direction: row-reverse;
  right: 0px;
  top: 0px;
  &:hover {
    fill: ${p => p.theme.colors.gray600};
  }
`;

const CloseIcon = styled(XIcon)`
  cursor: pointer;
  position: absolute;
  right: 20px;
  top: 20px;
  height: 16px;
  width: 16px;
  fill: ${p => p.theme.colors.gray500};
`;

const Modal = styled.div`
  text-align: center;
  .modalCard-content {
    padding: ${p => p.theme.spacing.large};
    position: relative;
  }
`;

export default EmbedModal;
